<script>
import {
  defineComponent,
  computed,
  watch,
  onMounted,
  ref,
} from '@vue/composition-api';

import { everything } from '@sejazipper/layer-api-client';
import { EventBus } from '@/event-bus.js';
import phoneSanitizer from '../resources/sanitizers/phone';
import EditSellersHeader from '../components/Sellers/EditSellerHeader.vue';
import EditSellerBasicInput from '../components/Sellers/EditSellerBasicInput.vue';
import EditSellerConfirmDialog from '../components/Sellers/EditSellerConfirmDialog.vue';
import EditSellerTooltip from '../components/Sellers/EditSellerTooltip.vue';
import EditSellerProfileTooltipContent from '../components/Sellers/EditSellerProfileTooltipContent.vue';
import EditSellerCodeTooltipContent from '../components/Sellers/EditSellerCodeTooltipContent.vue';
import EditSellerAltCodeTooltipContent from '../components/Sellers/EditSellerAltCodeTooltipContent.vue';
import { useZipperApi } from '../composables/useZipperApi';
import { useSellerManagement } from '../composables/useSellerManagement';
import { useVuexSellerStores } from '../composables/useVuexSellerStores';

const Promise = require('bluebird');
const _ = require('lodash');

export default defineComponent({
  components: {
    EditSellersHeader,
    EditSellerBasicInput,
    EditSellerConfirmDialog,
    EditSellerTooltip,
    EditSellerCodeTooltipContent,
    EditSellerProfileTooltipContent,
    EditSellerAltCodeTooltipContent,
  },
  setup(props, { root }) {
    const { client } = useZipperApi();
    const profile = computed(() => root.$store.getters.getProfile);
    const sellerId = root.$route.params.id;
    const customerId = root.$store.state.customer.id;
    const storeId = root.$store.state.store.id;
    const { isDirector } = root.$store.getters;
    const profiles = [
      { label: 'Vendedor', value: 'seller' },
      { label: 'Gerente', value: 'manager' },
      { label: 'Administrador', value: 'director', disabled: !isDirector },
    ];
    const seller = ref();
    const filteredProfiles = computed(() => profiles.filter((p) => !p.disabled));
    const selectedProfiles = ref([]);
    const applyProfiles = (s) => {
      profiles.forEach((p) => {
        if (s.value[p.value]) selectedProfiles.value.push(p);
      });
    };
    watch(selectedProfiles, (currentValue) => {
      profiles.forEach(({ value }) => { seller.value[value] = false; });
      currentValue.forEach(({ value }) => { seller.value[value] = true; });
    });
    const valid = true;
    const { isEditing, toggleEditing } = useSellerManagement();
    const { stores } = useVuexSellerStores(root);
    const sellerStores = ref();
    let initialStores = [];
    const isLoading = ref(false);
    const vMaskPhone = '(##) #####-####';
    const alternateIdsCombobox = ref(null);
    const rules = {
      required: (v) => !!v || 'Campo Obrigatório',
      profileRequired: (value) => (value.length > 0) || 'Deve ser selecionado pelo menos 1 tipo de perfil',
      requiredArray: (value) => value.length > 0 || 'Campo Obrigatório',
      min: (value) => (value && value.length >= 8) || 'A senha não atingiu o número mínimo de caracteress',
      profile: (value) => value.length > 0 || 'Campo Obrigatório',
      minCPF: (value) => !value
        || value.replace(/\D/g, '').length === 11
        || 'Minimo de 11 caracteres',
      confirmedPassword: (value) => value === seller.value.password || 'As senhas inseridas não correspondem',
    };
    const isNewSeller = computed(() => sellerId === 'new');
    const [showPassword, showConfirmPassword] = Array(2).fill(false);
    const emitSnackBar = (message = 'Dados editados com sucesso!') => {
      const snackBar = {};
      snackBar.text = message;
      snackBar.value = true;
      EventBus.$emit('activeSnackBar', snackBar);
    };
    const load = async () => {
      if (!isNewSeller.value) {
        const res = await Promise.all([
          client.chain.query
            .findSellerById({ id: sellerId })
            .get({ ...everything }),
          client.chain.query
            .findStoresBySellerId({ customerId, sellerId })
            .get({ ...everything }),
        ]);
        [seller.value, sellerStores.value] = res;
        initialStores = sellerStores.value;
        root.$set(
          seller.value,
          'fullNumber',
          `${seller.value.phoneArea}${seller.value.phone}`,
        );
      } else {
        const newSeller = {
          _id: '',
          name: '',
          email: '',
          termsOfUse: {},
          password: '',
          gender: '',
          nickname: '',
          director: false,
          manager: false,
          idERP: '',
          cpf: '',
          seller: false,
          countryCode: 0,
          phone: 0,
          phoneArea: 0,
          disabled: false,
          alternateIds: [],
          disabledCampaign: false,
          onlyDirectCustomers: false,
          storeId: '',
          voucher: '',
        };
        seller.value = newSeller;
      }
      applyProfiles(seller);
    };
    const removedStores = computed(() => _.differenceBy(initialStores, sellerStores.value, (obj) => obj._id || obj.id));
    const saveSellerStores = async (sellerSaved) => {
      const newStores = _.difference(sellerStores.value, initialStores);

      if (newStores && newStores.length) {
        let newStoresIds = newStores.map((s) => s._id || s.id);
        if (isNewSeller.value) {
          newStoresIds = newStoresIds.filter((id) => id !== storeId);
        }
        await client.chain.mutation
          .duplicateSeller({
            customerId,
            storeIds: newStoresIds,
            seller: sellerSaved,
          })
          .get({ name: 1 });
      }

      if (!isNewSeller.value && removedStores.value && removedStores.value.length) {
        await Promise.map(removedStores.value, async (removedStore) => {
          await client.chain.mutation
            .deleteSeller({
              customerId,
              storeId: removedStore._id,
              seller: sellerSaved,
            })
            .get({ name: 1 });
        });
      }
    };
    const reactivateSeller = async () => {
      try {
        isLoading.value = true;
        const sellerSaved = { ...seller.value };
        const phoneSanitized = await phoneSanitizer(sellerSaved.fullNumber);
        sellerSaved.phone = Number(phoneSanitized.phone);
        sellerSaved.phoneArea = Number(phoneSanitized.phoneArea);
        sellerSaved.disabled = !sellerSaved.disabled;
        sellerSaved.disabledCampaign = sellerSaved.disabled;
        delete sellerSaved.fullNumber;
        delete sellerSaved.__typename;
        await client.chain.mutation
          .updateFieldsSeller({
            customerId,
            seller: sellerSaved,
          })
          .get({ name: 1 });
        root.$router.push('/sellers');
        root.$store.dispatch('carregarPerfil', { user: root.$auth.user });
        isLoading.value = false;
        const snackBar = {};
        snackBar.text = 'Usuário reativado com sucesso!';
        snackBar.value = true;
        snackBar.color = '#140C22';
        snackBar.textColor = '#FFFFFF';
        EventBus.$emit('activeSnackBar', snackBar);
      } catch (err) {
        console.log(err);
        const snackBar = {};
        snackBar.text = 'Ocorreu um erro, tente novamente mais tarde.';
        snackBar.value = true;
        snackBar.color = 'red';
        snackBar.textColor = '#FFFFFF';
        EventBus.$emit('activeSnackBar', snackBar);
        isLoading.value = false;
      }
    };
    const handleGoBack = () => {
      root.$store.dispatch('carregarPerfil', { user: root.$auth.user });
      toggleEditing();
      root.$router.push('/sellers');
    };
    const getSellerLevel = (user) => {
      // eslint-disable-next-line no-nested-ternary
      const userLevel = (user.director || user.isDirector) ? 3 : (user.manager || user.isManager) ? 2 : 1;
      return userLevel;
    };
    const checkRolePermission = () => {
      if (getSellerLevel(root.$store.getters.getProfile) >= getSellerLevel(seller.value)) return true;
      return false;
    };
    const saveSeller = async () => {
      try {
        alternateIdsCombobox.value.blur();
        isLoading.value = true;
        const phoneSanitized = await phoneSanitizer(seller.value.fullNumber);
        seller.value.phone = Number(phoneSanitized.phone);
        seller.value.phoneArea = Number(phoneSanitized.phoneArea);
        const sellerSaved = { ...seller.value };
        delete sellerSaved.fullNumber;
        delete sellerSaved.__typename;
        sellerSaved.email = sellerSaved.email.toLowerCase();

        if (!isNewSeller.value) {
          await client.chain.mutation
            .updateFieldsSeller({
              customerId,
              seller: sellerSaved,
            })
            .get({ name: 1 });
          await saveSellerStores(sellerSaved);
          client.chain.mutation
            .updateActiveCampaignResults({
              customerId,
              seller: sellerSaved,
            }).get(true);
          isLoading.value = false;
          const snackBar = {};
          snackBar.text = 'Dados editados com sucesso!';
          snackBar.value = true;
          snackBar.color = '#140C22';
          snackBar.textColor = '#FFFFFF';
          EventBus.$emit('activeSnackBar', snackBar);
          toggleEditing();
        } else {
          delete sellerSaved.confirmPassword;
          await client.chain.mutation
            .insertSeller({
              customerId,
              storeId,
              seller: sellerSaved,
            })
            .get({ _id: true });
          await saveSellerStores(sellerSaved);
          const snackBar = {};
          snackBar.text = 'Usuário cadastrado com sucesso!';
          snackBar.value = true;
          snackBar.color = '#140C22';
          snackBar.textColor = '#FFFFFF';
          EventBus.$emit('activeSnackBar', snackBar);
          handleGoBack();
        }
      } catch (err) {
        const errorMessage = err.message.includes('"handled": true') ? err.message.match(/.*/)[0] : '';
        const snackBar = {};

        isLoading.value = false;
        snackBar.text = errorMessage || 'Ocorreu um erro, tente novamente mais tarde.';
        snackBar.value = true;
        snackBar.color = 'red';
        snackBar.textColor = '#FFFFFF';
        EventBus.$emit('activeSnackBar', snackBar);
        console.log(err);
      }
    };
    onMounted(async () => {
      if (isEditing.value) toggleEditing();
      if (isNewSeller.value) toggleEditing();
      await load();
    });
    return {
      emitSnackBar,
      alternateIdsCombobox,
      toggleEditing,
      saveSeller,
      reactivateSeller,
      vMaskPhone,
      isEditing,
      seller,
      sellerId,
      profile,
      rules,
      valid,
      stores,
      sellerStores,
      getSellerLevel,
      checkRolePermission,
      isLoading,
      isNewSeller,
      showPassword,
      showConfirmPassword,
      removedStores,
      profiles,
      filteredProfiles,
      selectedProfiles,
    };
  },
});
</script>

<template>
  <v-container
    class="sellers px-4"
    style="min-height: 100vh"
  >
    <EditSellersHeader />
    <v-form
      ref="form"
      v-model="valid"
    >
      <div class="sellers__div mt-3 pa-3">
        <v-layout
          row
          wrap
          justify-space-between
          align-center
        >
          <span class="sellers__div__title"> Informações básicas </span>
          <v-chip
            class="sellers__status-chip px-3"
            :color="seller.disabled ? '#F1EFEC' : '#F4FFBA'"
            disabled
          >
            {{ seller.disabled ? 'Suspenso' : 'Ativo' }}
          </v-chip>
        </v-layout>
        <EditSellerBasicInput
          icon="$vuetify.icons.userIcon"
          class="mt-3"
        >
          <v-text-field
            v-model="seller.name"
            :disabled="!isEditing"
            clearable
            label="Nome"
            :rules="[rules.required]"
          />
        </EditSellerBasicInput>
        <EditSellerBasicInput icon="$vuetify.icons.atSignIcon">
          <v-text-field
            v-model="seller.nickname"
            :disabled="!isEditing"
            clearable
            label="Apelido"
            :rules="[rules.required]"
          />
        </EditSellerBasicInput>
        <span
          v-show="isEditing"
          class="sellers__div__field-description"
        >
          *O apelido é o nome que aparece para o cliente no envio de campanhas
          de WhatsApp
        </span>
      </div>
      <div class="sellers__div mt-3 pa-3">
        <v-layout
          row
          wrap
          justify-space-between
          align-center
        >
          <span class="sellers__div__title"> Informações de contato </span>
        </v-layout>
        <EditSellerBasicInput
          icon="$vuetify.icons.phoneIcon"
          class="mt-3"
        >
          <v-text-field
            v-model="seller.fullNumber"
            :mask="vMaskPhone"
            :disabled="!isEditing"
            clearable
            label="Whatsapp"
            :rules="[rules.required]"
          />
        </EditSellerBasicInput>
        <EditSellerBasicInput icon="$vuetify.icons.mailIcon">
          <v-text-field
            v-model="seller.email"
            :disabled="!isEditing"
            clearable
            label="E-mail"
            :rules="[rules.required]"
          />
        </EditSellerBasicInput>
      </div>
      <div
        v-if="isNewSeller"
        class="sellers__div mt-3 pa-3"
      >
        <v-layout
          row
          wrap
          justify-space-between
          align-center
        >
          <span class="sellers__div__title"> Senha </span>
        </v-layout>
        <EditSellerBasicInput
          icon="$vuetify.icons.passwordLockIcon"
          class="mt-3"
        >
          <v-text-field
            v-model="seller.password"
            :disabled="!isEditing"
            label="Senha (8 caracteres ou mais)"
            :rules="[rules.required, rules.min]"
            :append-icon="
              showPassword
                ? 'visibility'
                : 'visibility_off'
            "
            :type="showPassword ? 'text' : 'password'"
            @click:append="showPassword = !showPassword"
          />
        </EditSellerBasicInput>
        <EditSellerBasicInput icon="$vuetify.icons.passwordLockIcon">
          <v-text-field
            v-model="seller.confirmPassword"
            :disabled="!isEditing"
            label="Confirmar senha"
            :rules="[rules.required, rules.min, rules.confirmedPassword]"
            :append-icon="
              showConfirmPassword
                ? 'visibility'
                : 'visibility_off'
            "
            :type="showConfirmPassword ? 'text' : 'password'"
            @click:append="showConfirmPassword = !showConfirmPassword"
          />
        </EditSellerBasicInput>
      </div>
      <div class="sellers__div mt-3 pa-3">
        <v-layout
          row
          wrap
          justify-space-between
          align-center
          class="mb-4"
        >
          <span class="sellers__div__title"> Identificação </span>
        </v-layout>
        <EditSellerBasicInput
          icon="$vuetify.icons.pIcon"
        >
          <v-layout
            row
            :class="isEditing ? 'v-input v-label' : 'theme--light v-input v-label'"
            align-center
          >
            <span class="mr-2">
              Perfil
            </span>
            <span>
              <EditSellerTooltip>
                <EditSellerProfileTooltipContent />
              </EditSellerTooltip>
            </span>
          </v-layout>
          <div v-show="isEditing">
            <v-select
              v-model="selectedProfiles"
              :disabled="!isEditing"
              :items="filteredProfiles"
              item-text="label"
              multiple
              chips
              small-chips
              deletable-chips
              return-object
              :rules="[rules.profileRequired]"
            />
          </div>
          <div v-show="!isEditing">
            <v-chip
              v-show="seller.director"
              outline
              color="#000000"
            >
              Administrador
            </v-chip>
            <v-chip
              v-show="seller.manager"
              outline
              color="#000000"
            >
              Gerente
            </v-chip>
            <v-chip
              v-show="seller.seller"
              outline
              color="#000000"
            >
              Vendedor
            </v-chip>
          </div>
        </EditSellerBasicInput>
        <EditSellerBasicInput icon="$vuetify.icons.bagIcon">
          <v-autocomplete
            v-model="sellerStores"
            :disabled="!isEditing"
            :items="stores"
            item-text="name"
            label="Loja(s)"
            multiple
            chips
            small-chips
            deletable-chips
            return-object
            :rules="[rules.requiredArray]"
          />
        </EditSellerBasicInput>
        <EditSellerBasicInput icon="$vuetify.icons.barCodeIcon">
          <v-layout
            row
            :class="isEditing ? 'v-input v-label' : 'theme--light v-input v-label'"
            style="margin-bottom: -16px"
            align-center
          >
            <span class="mr-2">
              Código de Vendas
            </span>
            <span>
              <EditSellerTooltip>
                <EditSellerCodeTooltipContent />
              </EditSellerTooltip>
            </span>
          </v-layout>
          <v-text-field
            v-model="seller.idERP"
            :disabled="!isEditing"
            clearable
          />
        </EditSellerBasicInput>
        <EditSellerBasicInput icon="$vuetify.icons.barCodeIcon">
          <v-layout
            row
            wrap
            align-center
          >
            <v-flex xs11>
              <span
                row
                :class="isEditing ? 'v-input v-label' : 'theme--light v-input v-label'"
                style="margin-bottom: -16px; align-items: center"
              >
                <span class="mr-2">
                  Código de Vendas Alternativo
                </span>
                <span>
                  <EditSellerTooltip>
                    <EditSellerAltCodeTooltipContent />
                  </EditSellerTooltip>
                </span>
              </span>
              <v-combobox
                ref="alternateIdsCombobox"
                v-model="seller.alternateIds"
                :disabled="!isEditing"
                multiple
                chips
                deletable-chips
                append-icon=""
              />
            </v-flex>
            <v-flex xs1>
              <v-btn
                flat
                icon
                color="#140C22"
                style="margin-left: -5px"
              >
                <v-icon>mdi-plus-circle</v-icon>
              </v-btn>
            </v-flex>
          </v-layout>
        </EditSellerBasicInput>
        <EditSellerBasicInput icon="$vuetify.icons.localActivityIcon">
          <v-text-field
            v-model="seller.voucher"
            label="Voucher"
            :disabled="!isEditing"
            clearable
          />
        </EditSellerBasicInput>
      </div>
      <v-btn
        v-if="isEditing && !removedStores.length"
        :disabled="!valid"
        :loading="isLoading"
        block
        class="mt-4 sellers__actions-btn text-capitalize"
        style="color: white"
        color="#140C22"
        @click="saveSeller()"
      >
        Salvar
      </v-btn>
      <v-btn
        v-if="!isEditing && seller.disabled"
        :loading="isLoading"
        block
        class="mt-4 mb-3 sellers__actions-btn text-capitalize"
        style="color: white"
        color="#140C22"
        :disabled="!checkRolePermission()"
        @click="reactivateSeller()"
      >
        Reativar
      </v-btn>
      <EditSellerConfirmDialog
        v-if="!isEditing && !seller.disabled"
        class="mt-4"
        operation="suspension"
        :seller="seller"
        :is-loading="isLoading"
        :disabled="!checkRolePermission()"
      />
      <EditSellerConfirmDialog
        v-if="!isEditing && !profile.cannotCreateUser"
        operation="exclusion"
        :seller="seller"
        :stores-to-delete-from="stores"
        :is-loading="isLoading"
        :disabled="!checkRolePermission()"
      />
      <EditSellerConfirmDialog
        v-if="isEditing && removedStores.length"
        class="mt-4"
        operation="changeStore"
        :seller="seller"
        :is-loading="isLoading"
        :disabled="!checkRolePermission()"
        @save-seller="saveSeller()"
      />
    </v-form>
  </v-container>
</template>

<style lang="scss" scoped>
.sellers {
  margin-bottom: 60pt;
  &__div {
    background: #ffffff;
    border: 1px solid #eeeeee;
    box-sizing: border-box;
    box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.1);
    border-radius: 7px;
    &__title {
      font-family: "Nunito Sans";
      font-style: normal;
      font-weight: 600;
      font-size: 16px;
      line-height: 24px;
      color: #000000;
    }
    &__field-description {
      font-family: "Nunito Sans";
      font-style: normal;
      font-weight: 400;
      font-size: 10px;
      line-height: 14px;
      color: rgba(0, 0, 0, 0.5);
    }
  }
  &__status-chip {
    font-family: "Neue-Metana";
    font-style: normal;
    font-weight: 400;
    font-size: 10px;
    line-height: 12px;
    color: #140c22;
  }
  &__actions-btn {
    border-radius: 7.08982px;
    font-size: 16px;
    height: 41.92px !important;
  }
  .v-input--selection-controls {
    margin-top: 5px;
    padding-top: 0px;
    height: 30px;
    font-family: "Nunito Sans";
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 22px;
  }
}
</style>
